import type React from 'react'

import type { CreatorPermission } from '@api'
import { useUnit } from 'effector-react'
import { Outlet } from 'react-router'
import { NavLink } from 'react-router-dom'

import { Tooltip } from '@/components/ui/tooltip.tsx'
import { $account } from '@/models/account.ts'
import { $creators, $currentCreator } from '@/models/creators.ts'
import { hasPermissionsToRoute } from '@/modules/accounts/state/AccessControlGuard.tsx'
import type { CreatorFeatures } from '@/modules/accounts/types'
import { classNames } from '@/utils/helpers.ts'

interface SubSidebarProps {
  routes: SubRoute[]
}

export interface SubRoute {
  name: string
  href: string
  icon?: React.ReactNode
  creatorPermission?: CreatorPermission
  creatorFeature?: CreatorFeatures
  accountFeature?: CreatorFeatures
  testId?: string
}

export const SubSidebar = ({ routes }: SubSidebarProps) => {
  const creators = useUnit($creators)
  const activeCreator = useUnit($currentCreator)
  const account = useUnit($account)

  const linkClasses =
    'truncate flex items-center gap-2 text-sm text-title font-medium py-2 px-4 rounded-md aria-[current=page]:bg-primary-500 aria-[current=page]:text-white dark:text-white transition duration-300 ease-out'

  return (
    <>
      <div className="fixed left-0 top-[72px] z-10 h-[calc(100vh-72px)] w-[80px] lg:left-[300px] xl:w-[240px]">
        <div className="bg-card flex h-full flex-col gap-3 border-r px-4 py-8">
          {routes.map((item, index) => {
            const hasPermissions = hasPermissionsToRoute(
              activeCreator,
              item.creatorPermission,
              item.creatorFeature,
              account,
              item.accountFeature,
              creators,
            )

            if (!hasPermissions) {
              return (
                <Tooltip
                  key={item.name}
                  content="No permission to access this page"
                  className={classNames(
                    linkClasses,
                    'cursor-default text-gray-500',
                  )}
                  testId={item.testId}
                >
                  <span className="hidden xl:block">{item.icon}</span>
                  <span className="hidden truncate xl:block">{item.name}</span>
                </Tooltip>
              )
            }

            return (
              <NavLink
                key={index}
                to={item.href.replace(
                  ':creatorId',
                  String(activeCreator?.id ?? 0),
                )}
                className={classNames(
                  linkClasses,
                  'hover:bg-neutral-contrast dark:hover:bg-secondary-600',
                )}
                data-testid={item.testId}
              >
                <Tooltip
                  className="xl:hidden"
                  side="right"
                  sideOffset={20}
                  content={item.name}
                >
                  {item.icon}
                </Tooltip>
                <span className="hidden xl:block">{item.icon}</span>
                <span className="hidden truncate xl:block">{item.name}</span>
              </NavLink>
            )
          })}
        </div>
      </div>
      <div className="ml-[80px] h-full flex-1 xl:ml-[240px]">
        <Outlet />
      </div>
    </>
  )
}

export const Attention = (props: {
  id: string
  disabled: boolean
  children: React.ReactNode
}) => {
  const { disabled, children } = props

  return (
    <div className="relative">
      {children}
      {!disabled && (
        <div className="pointer-events-none absolute left-0 top-0 flex h-full w-full items-center justify-center xl:hidden">
          <span className="sr-only">Attention</span>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-500/10" />
          <span className="absolute inline-flex h-[100%] w-[100%] animate-pulse rounded-full bg-yellow-500/20" />
          <span className="absolute inline-flex h-[80%] w-[80%] animate-pulse rounded-full bg-yellow-500/20" />
        </div>
      )}
    </div>
  )
}
